import { computed, unref } from "vue";
import { useI18n } from "vue-i18n";

import {
  EMPTY_VALUE,
  capitalize,
  daysFromToday,
  isBlank,
  relationLabel,
  shortDate,
} from "@/utils/formatters";

export const PENDING_STATES = ["not_paid", "partial"];
export const PAID_STATES = ["paid", "reversed"];

const STATE_LABELS = {
  draft: "invoices.state_draft",
  posted: "invoices.state_posted",
  cancel: "invoices.state_cancel",
};

const TYPE_FLAGS = [
  { key: "factura_reconexion", label: "invoices.tag_reconnection" },
  { key: "factura_penalizacion", label: "invoices.tag_penalty" },
  { key: "factura_por_equipo", label: "invoices.tag_equipment" },
  { key: "factura_por_excedente", label: "invoices.tag_excess" },
];

/**
 * Everything the invoice card and the invoice detail view need to describe an
 * invoice: payment status, due-date wording, readable state and type tags.
 *
 * `source` may be a ref or a getter, so components can pass `() => props.x`.
 */
export default function useInvoiceStatus(source) {
  const { locale, t } = useI18n();

  const invoice = computed(() => {
    const value = typeof source === "function" ? source() : unref(source);
    return value || {};
  });

  const formatDate = (value) => shortDate(value, locale.value);

  const residual = computed(() => Number(invoice.value.amount_residual) || 0);

  const isPaid = computed(() =>
    PAID_STATES.includes(invoice.value.payment_state)
  );
  const isCancelled = computed(() => invoice.value.state === "cancel");
  // Flagged by the list view when the invoice shows up in the payment history
  // but the backend has not confirmed the payment yet
  const isProcessing = computed(
    () => !!invoice.value.payment_status && !isPaid.value
  );

  const isOverdue = computed(() => {
    const days = daysFromToday(invoice.value.invoice_date_due);

    return (
      PENDING_STATES.includes(invoice.value.payment_state) &&
      !isCancelled.value &&
      !isProcessing.value &&
      days !== null &&
      days < 0
    );
  });

  const status = computed(() => {
    if (isPaid.value) {
      return { variant: "success", label: t("invoices.paid") };
    }
    if (isCancelled.value) {
      return { variant: "neutral", label: t("invoices.cancelled") };
    }
    if (isProcessing.value) {
      return { variant: "info", label: t("invoices.pending_payment") };
    }
    if (isOverdue.value) {
      return { variant: "danger", label: t("invoices.overdue") };
    }

    return { variant: "warning", label: t("invoices.not_payed") };
  });

  const due = computed(() => {
    if (isPaid.value && !isBlank(invoice.value.fecha_pagado)) {
      return {
        variant: "is-paid",
        icon: "uil-check-circle",
        label: t("invoices.paid_on", {
          date: formatDate(invoice.value.fecha_pagado),
        }),
      };
    }

    const date = formatDate(invoice.value.invoice_date_due);

    if (isOverdue.value) {
      return {
        variant: "is-overdue",
        icon: "uil-exclamation-circle",
        label: t("invoices.overdue_on", { date }),
      };
    }

    return {
      variant: "",
      icon: "uil-calendar-alt",
      label: t("invoices.due_on", { date }),
    };
  });

  const stateLabel = computed(() => {
    const key = STATE_LABELS[invoice.value.state];
    if (key) {
      return t(key);
    }

    return isBlank(invoice.value.state)
      ? EMPTY_VALUE
      : capitalize(String(invoice.value.state));
  });

  const paymentTerm = computed(() =>
    relationLabel(invoice.value.invoice_payment_term_id)
  );

  const tags = computed(() => {
    const result = TYPE_FLAGS.filter((flag) => invoice.value[flag.key]).map(
      (flag) => ({ key: flag.key, label: t(flag.label), variant: "" })
    );

    if (invoice.value.l10n_mx_edi_cfdi_state === "cancel") {
      result.push({
        key: "cfdi",
        label: t("invoices.cfdi_cancelled"),
        variant: "danger",
      });
    }

    return result;
  });

  return {
    due,
    formatDate,
    isOverdue,
    isPaid,
    paymentTerm,
    residual,
    stateLabel,
    status,
    tags,
  };
}
